import clsx from 'clsx';
import { IngredientsImageGrid } from '@hungryroot/pho';
import { Paragraph } from '@hungryroot/ramen';
import {
  LikeDislike,
  LikeDislikeSize,
  LikeDislikeVariant,
} from '../../BinaryRatings/LikeDislike/LikeDislike';
import {
  RatableItemType,
  type RatablePairingItem,
  type RatableProductItem,
} from '../useRatableItems';

import styles from './RatableItem.module.css';

type RatableItemProps = {
  className?: string;
  item: RatablePairingItem | RatableProductItem;
  onRating: (prev: boolean | null, next: boolean | null) => void;
  rating: boolean | null;
  testid?: string;
};

export function RatableItem(props: RatableItemProps) {
  const { className, item, onRating, rating, testid } = props;

  return (
    <div
      className={clsx(styles['ratable-item'], className)}
      data-testid={testid}
    >
      <div className={styles['image-container']}>
        <RatableItemImg item={item} />
      </div>
      <Paragraph className={styles.name} variant="body">
        {item.name}
      </Paragraph>
      <LikeDislike
        className={styles['like-dislike']}
        buttonClassName={clsx(
          styles['like-dislike-button'],
          styles['like-dislike-button-unrated']
        )}
        ratedButtonClassName={clsx(
          styles['like-dislike-button'],
          styles['like-dislike-button-rated']
        )}
        onRating={(next) => {
          if (typeof next === 'boolean' || next === null) {
            onRating(rating, next);
          }
        }}
        size={LikeDislikeSize.Small}
        variant={LikeDislikeVariant.Pairing}
        rating={rating}
        testid="rating-container"
      />
    </div>
  );
}

function RatableItemImg(props: { item: RatableItemProps['item'] }) {
  const { item } = props;

  const { src, alt, ingredients } = getRatableItemImgInfo(item);

  if (ingredients) {
    return (
      <IngredientsImageGrid
        className={styles['image']}
        ingredients={ingredients}
      />
    );
  }

  if (src) {
    return <img className={styles['image']} alt={alt} src={src} />;
  }
}

function getRatableItemImgInfo(item: RatableItemProps['item']) {
  if (item.type === RatableItemType.Pairing && !item.featuredImgUrl) {
    return {
      ingredients: item.ingredients,
    };
  }

  const alt = item.name;

  let src;

  if (item.type === RatableItemType.Product) {
    src = item.cardImageUrl || item.thumbImageUrl;
  } else {
    src = item.featuredImgUrl;
  }

  return { src, alt };
}
